import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Suspense, useState } from "react";
import z from "zod";

import { isAdminRole, useViewer, useViewerHasPermission } from "@/auth/shared";
import type {
  Director,
  PlayerProfile,
  TournamentDirector,
  Venue,
  VenueDirector,
} from "@/db/schema";
import type { LexicalState } from "@/db/schema/shared";
import { useTRPC } from "@/integrations/trpc/react";
import { formatPhone } from "@/lib/phone";

import { useAppForm } from "../base/form";
import { subtitle } from "../base/primitives";
import { Radio, RadioGroup } from "../base/radio-group";
import { RichTextDisplay } from "../base/rich-text-editor/display";
import { queue } from "../base/toast";
import { AddDirector } from "../directors/add";
import { EditDirector } from "../directors/edit";
import { RemoveDirector } from "../directors/remove";
import { TournamentImage } from "../tournaments/tournament-image";

type DirectorWithProfile = Director & {
  profile: PlayerProfile & {
    user?: { phoneNumber: string | null } | null;
  };
};

export type VenueDisplayProps<TournamentId extends number | undefined> = {
  tournamentId?: TournamentId;
  tournamentDescription?: LexicalState | null;
  tournamentImageSource?: string | null;
  divisionId?: number;
  divisionDescription?: LexicalState | null;
  venue: Pick<
    Venue,
    | "id"
    | "name"
    | "city"
    | "description"
    | "directions"
    | "mapUrl"
    | "withdrawDeadlineHours"
    | "defaultAutopromoteWaitlist"
    | "registrationOpenDaysBefore"
  > & {
    directors: ((TournamentId extends number
      ? TournamentDirector
      : VenueDirector) & {
      director: DirectorWithProfile;
    })[];
  };
  onEditSuccess?: () => void;
};

export function VenueDisplay<TournamentId extends number | undefined>({
  tournamentId,
  tournamentDescription,
  tournamentImageSource,
  divisionId,
  divisionDescription,
  venue,
  onEditSuccess,
}: VenueDisplayProps<TournamentId>) {
  const hasEditPermission = useViewerHasPermission({
    venues: ["update"],
  });

  const viewer = useViewer();

  const isViewerDirector =
    viewer &&
    venue.directors.some(
      ({ director }) => director.profile.userId === viewer.id,
    );

  const isAdmin = isAdminRole(viewer?.role);

  const canEdit = hasEditPermission && (isViewerDirector || isAdmin);

  const trpc = useTRPC();

  const { mutateAsync: updateVenue } = useMutation({
    ...trpc.venues.updateContent.mutationOptions(),
    onSuccess: onEditSuccess,
  });
  const { mutateAsync: updateTournamentDescription } = useMutation({
    ...trpc.tournaments.updateDescription.mutationOptions(),
    onSuccess: onEditSuccess,
  });

  const { mutateAsync: updateDivisionDescription } = useMutation({
    ...trpc.tournaments.updateDivisionDescription.mutationOptions(),
    onSuccess: onEditSuccess,
  });

  const { mutateAsync: updateTournamentImage } = useMutation({
    ...trpc.tournaments.updateImage.mutationOptions(),
    onSuccess: onEditSuccess,
  });

  const handleImageChange = tournamentId
    ? async (src: string | null) => {
        await updateTournamentImage({
          tournamentId,
          imageSource: src,
        });
      }
    : undefined;

  const [saveTarget, setSaveTarget] = useState<
    "division" | "tournament" | "venue"
  >(
    divisionDescription
      ? "division"
      : tournamentDescription
        ? "tournament"
        : "venue",
  );

  const displayDescription =
    divisionDescription ?? tournamentDescription ?? venue.description;

  return (
    <Suspense>
      <div className="mx-auto max-w-4xl px-3 pt-12">
        {tournamentId && handleImageChange && (
          <div className="mb-4 sm:hidden">
            <TournamentImage
              imageSource={tournamentImageSource ?? null}
              canEdit={!!canEdit}
              onImageChange={handleImageChange}
            />
          </div>
        )}

        <h4 className={subtitle()}>Director Notes</h4>

        <div className="flex flex-col gap-3 pb-12 sm:flex-row">
          <RichTextDisplay
            name="venue-description"
            content={
              typeof displayDescription === "string" && displayDescription
                ? JSON.parse(displayDescription)
                : displayDescription || undefined
            }
            onSave={
              canEdit
                ? async (state) => {
                    if (
                      tournamentId &&
                      divisionId &&
                      saveTarget === "division"
                    ) {
                      await updateDivisionDescription({
                        tournamentDivisionId: divisionId,
                        description: state as LexicalState,
                      });
                    } else if (tournamentId && saveTarget === "tournament") {
                      await updateTournamentDescription({
                        tournamentId,
                        description: state as LexicalState,
                      });
                    } else if (tournamentId) {
                      // Save to venue + clear tournament override in one call
                      await updateTournamentDescription({
                        tournamentId,
                        venueDescription: state as LexicalState,
                      });
                    } else {
                      await updateVenue({
                        id: venue.id,
                        description: state as LexicalState,
                      });
                    }
                  }
                : undefined
            }
            footer={
              tournamentId && canEdit ? (
                <RadioGroup
                  value={saveTarget}
                  orientation={divisionId ? "vertical" : "horizontal"}
                  onChange={(value) => {
                    setSaveTarget(value as "division" | "tournament" | "venue");
                  }}
                >
                  {divisionId && (
                    <Radio value="division">Save for this division only</Radio>
                  )}
                  <Radio value="tournament">
                    Save for this tournament only
                  </Radio>
                  <Radio value="venue">Save as default for this location</Radio>
                </RadioGroup>
              ) : undefined
            }
          />

          <div className="relative flex min-w-3/12 flex-col space-y-3">
            {tournamentId && handleImageChange && (
              <div className="hidden sm:block">
                <TournamentImage
                  imageSource={tournamentImageSource ?? null}
                  canEdit={!!canEdit}
                  onImageChange={handleImageChange}
                />
              </div>
            )}

            <h5 className="flex flex-row items-center justify-between font-semibold">
              <span>Director</span>

              {canEdit && (
                <AddDirector
                  mode={tournamentId ? "tournament" : "venue"}
                  targetId={tournamentId ?? venue.id}
                  existingDirectorIds={venue.directors.map((d) => d.directorId)}
                />
              )}
            </h5>

            <div className="flex flex-col space-y-2">
              {venue.directors.map((td) => {
                const name = `${td.director.profile.preferredName || td.director.profile.firstName} ${td.director.profile.lastName}`;

                return (
                  <ul key={td.id} className="relative">
                    <li className="relative flex flex-row items-center justify-between overflow-ellipsis whitespace-nowrap">
                      <span>{name}</span>

                      {canEdit &&
                        (isAdmin ||
                          td.director.profile.userId === viewer?.id) && (
                          <div className="flex flex-row items-center">
                            <EditDirector
                              directorId={td.director.id}
                              name={name}
                              email={td.director.email}
                              phoneNumber={td.director.phoneNumber}
                              hidePhoneNumber={td.director.hidePhoneNumber}
                            />
                            <RemoveDirector
                              id={td.id}
                              name={name}
                              mode={tournamentId ? "tournament" : "venue"}
                              targetId={tournamentId ?? venue.id}
                              isDisabled={venue.directors.length === 1}
                            />
                          </div>
                        )}
                    </li>
                    {td.director.email && <li>{td.director.email}</li>}
                    {!td.director.hidePhoneNumber &&
                      (td.director.phoneNumber ||
                        td.director.profile.user?.phoneNumber) && (
                        <li>
                          {formatPhone(
                            td.director.phoneNumber ||
                              td.director.profile.user?.phoneNumber,
                          )}
                        </li>
                      )}
                  </ul>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-300 py-12">
          <h4 className={subtitle()}>Directions</h4>

          <RichTextDisplay
            name="venue-directions"
            content={
              typeof venue.directions === "string" && venue.directions
                ? JSON.parse(venue.directions)
                : venue.directions || undefined
            }
            onSave={
              canEdit
                ? async (state) => {
                    await updateVenue({
                      id: venue.id,
                      directions: state as LexicalState,
                    });
                  }
                : undefined
            }
          />
        </div>
      </div>
      {canEdit && !tournamentId && (
        <div className="mx-auto max-w-4xl border-t border-gray-300 px-3 py-12">
          <h4 className={subtitle()}>Venue Defaults</h4>
          <VenueDefaultsForm
            venueId={venue.id}
            withdrawDeadlineHours={venue.withdrawDeadlineHours}
            defaultAutopromoteWaitlist={venue.defaultAutopromoteWaitlist}
            registrationOpenDaysBefore={venue.registrationOpenDaysBefore}
          />
        </div>
      )}

      {venue.mapUrl?.startsWith("https://www.google.com") && (
        <iframe
          title="google maps"
          loading="lazy"
          height={350}
          style={{ border: "none", width: "100%", outline: "none" }}
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src={venue.mapUrl}
        />
      )}
    </Suspense>
  );
}

const venueDefaultsSchema = z.object({
  withdrawDeadlineHours: z.number().int().min(0),
  defaultAutopromoteWaitlist: z.boolean(),
  registrationOpenDaysBefore: z.number().int().min(0).nullable(),
});

function VenueDefaultsForm({
  venueId,
  withdrawDeadlineHours,
  defaultAutopromoteWaitlist,
  registrationOpenDaysBefore,
}: {
  venueId: number;
  withdrawDeadlineHours: number;
  defaultAutopromoteWaitlist: boolean;
  registrationOpenDaysBefore: number | null;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.venues.updateDefaults.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(
        trpc.venues.get.queryOptions({ id: venueId }),
      );
      queue.add({
        variant: "success",
        title: "Updated",
        description: "Venue defaults have been updated.",
      });
    },
  });

  const form = useAppForm({
    defaultValues: {
      withdrawDeadlineHours,
      defaultAutopromoteWaitlist,
      registrationOpenDaysBefore,
    } as z.infer<typeof venueDefaultsSchema>,
    validators: {
      onMount: venueDefaultsSchema,
      onChange: venueDefaultsSchema,
    },
    onSubmit: ({ value }) => {
      mutate({ id: venueId, ...value });
    },
  });

  return (
    <form
      className="mt-3 flex max-w-sm flex-col space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      {failureReason && (
        <form.AppForm>
          <form.Alert
            title="Unable to update defaults"
            description={failureReason.message}
          />
        </form.AppForm>
      )}

      <form.AppField name="withdrawDeadlineHours">
        {(field) => (
          <field.Number
            field={field}
            label="Withdraw Deadline (hours before start)"
            description="Hours before tournament start that teams can withdraw with a refund"
          />
        )}
      </form.AppField>

      <form.AppField name="defaultAutopromoteWaitlist">
        {(field) => (
          <field.Checkbox
            field={field}
            label="Auto-promote from waitlist"
            description="Automatically promote waitlisted teams when a spot opens up"
          />
        )}
      </form.AppField>

      <form.AppField name="registrationOpenDaysBefore">
        {(field) => (
          <field.Number
            field={field}
            label="Registration Opens (days before tournament)"
            description="How many days before the tournament date registration should open. Leave blank for no automatic opening."
          />
        )}
      </form.AppField>

      <form.AppForm>
        <form.Footer>
          <form.SubmitButton>Save</form.SubmitButton>
        </form.Footer>
      </form.AppForm>
    </form>
  );
}
